import { computed, type ComputedRef } from 'vue';

import {
  getBooleanTokenClaim,
  getDirectStringTokenClaim,
  getNamespacedStringTokenClaim,
} from '../store/session/token-claims';
import { useAuthSessionStore } from '../store/auth-session.store';

const EMAIL_VERIFIED_CLAIM = 'email_verified';
const PHONE_VERIFICATION_CLAIMS = ['phone_verified', 'phone_number_verified'] as const;

export type ActiveIdentitySession = {
  hasSession: boolean;
  userId: string | null;
  email: string | null;
  identityKey: string | null;
};

export type ActiveIdentityVerification = {
  hasEmailConfirmationClaim: boolean;
  isEmailConfirmed: boolean;
  hasPhoneVerificationClaim: boolean;
  isPhoneVerified: boolean;
};

export type ActiveIdentityPhone = {
  phoneNumber: string | null;
};

export type ActiveIdentityState = {
  session: ComputedRef<ActiveIdentitySession>;
  verification: ComputedRef<ActiveIdentityVerification>;
  phone: ComputedRef<ActiveIdentityPhone>;
  hasSession: ComputedRef<boolean>;
  userId: ComputedRef<string | null>;
  email: ComputedRef<string | null>;
  identityKey: ComputedRef<string | null>;
  hasEmailConfirmationClaim: ComputedRef<boolean>;
  isEmailConfirmed: ComputedRef<boolean>;
  hasPhoneVerificationClaim: ComputedRef<boolean>;
  isPhoneVerified: ComputedRef<boolean>;
  phoneNumber: ComputedRef<string | null>;
};

export const useActiveIdentity = (): ActiveIdentityState => {
  const authSessionStore = useAuthSessionStore();

  const session = computed<ActiveIdentitySession>(() => {
    const hasSession = !!authSessionStore.jwtToken;

    if (!hasSession) {
      return {
        hasSession,
        userId: null,
        email: null,
        identityKey: null,
      };
    }

    // App JWT claims are canonical for access/onboarding decisions. Missing claims must remain
    // missing rather than falling back to the Auth0 SDK profile.
    const userId = getDirectStringTokenClaim(authSessionStore.jwtToken, 'sub');
    const email = getDirectStringTokenClaim(authSessionStore.jwtToken, 'email');

    return {
      hasSession,
      userId,
      email,
      identityKey: userId,
    };
  });

  const verification = computed<ActiveIdentityVerification>(() => {
    const emailConfirmationClaim = getBooleanTokenClaim(
      authSessionStore.idToken,
      EMAIL_VERIFIED_CLAIM,
    );
    const phoneVerificationClaim = getBooleanTokenClaim(
      authSessionStore.idToken,
      PHONE_VERIFICATION_CLAIMS,
    );

    return {
      hasEmailConfirmationClaim: emailConfirmationClaim !== null,
      isEmailConfirmed: emailConfirmationClaim === true,
      hasPhoneVerificationClaim: phoneVerificationClaim !== null,
      isPhoneVerified: phoneVerificationClaim === true,
    };
  });

  const phone = computed<ActiveIdentityPhone>(() => {
    if (!authSessionStore.jwtToken) {
      return { phoneNumber: null };
    }

    const idTokenPhoneNumber = getNamespacedStringTokenClaim(
      authSessionStore.idToken,
      'phone_number',
    );

    if (idTokenPhoneNumber) {
      return { phoneNumber: idTokenPhoneNumber };
    }

    return {
      phoneNumber: getNamespacedStringTokenClaim(authSessionStore.jwtToken, 'phone_number'),
    };
  });

  const hasSession = computed(() => session.value.hasSession);
  const userId = computed(() => session.value.userId);
  const email = computed(() => session.value.email);
  const identityKey = computed(() => session.value.identityKey);
  const hasEmailConfirmationClaim = computed(() => verification.value.hasEmailConfirmationClaim);
  const isEmailConfirmed = computed(() => verification.value.isEmailConfirmed);
  const hasPhoneVerificationClaim = computed(() => verification.value.hasPhoneVerificationClaim);
  const isPhoneVerified = computed(() => verification.value.isPhoneVerified);
  const phoneNumber = computed(() => phone.value.phoneNumber);

  return {
    session,
    verification,
    phone,
    hasSession,
    userId,
    email,
    identityKey,
    hasEmailConfirmationClaim,
    isEmailConfirmed,
    hasPhoneVerificationClaim,
    isPhoneVerified,
    phoneNumber,
  };
};
